export function formatDate(value: string | number | Date, locale = 'en-US') {
  const date = new Date(value);
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

export function formatDateTime(value: string | number | Date, locale = 'en-US') {
  const date = new Date(value);
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function formatTime(value: string | number | Date, locale = 'en-US') {
  const date = new Date(value);
  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

export function formatRelative(target: string | number | Date, from: string | number | Date = Date.now()) {
  const targetDate = new Date(target);
  const fromDate = new Date(from);
  const diffMs = targetDate.getTime() - fromDate.getTime();

  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  const divisions: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 1000 * 60 * 60 * 24 * 365],
    ['month', 1000 * 60 * 60 * 24 * 30],
    ['week', 1000 * 60 * 60 * 24 * 7],
    ['day', 1000 * 60 * 60 * 24],
    ['hour', 1000 * 60 * 60],
    ['minute', 1000 * 60],
    ['second', 1000],
  ];

  for (const [unit, ms] of divisions) {
    const amount = diffMs / ms;
    if (Math.abs(amount) >= 1 || unit === 'second') {
      return rtf.format(Math.round(amount), unit);
    }
  }
  return rtf.format(0, 'second');
}

export function formatDuration(ms: number, options: { precision?: 'seconds' | 'minutes' | 'hours' | 'days' } = {}) {
  const { precision = 'seconds' } = options;
  const abs = Math.abs(ms);
  const seconds = Math.floor(abs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  const parts: string[] = [];
  if (precision === 'days') {
    parts.push(`${days}d`);
  } else if (precision === 'hours') {
    parts.push(`${hours}h`);
    parts.push(`${minutes % 60}m`);
  } else if (precision === 'minutes') {
    parts.push(`${minutes}m`);
    parts.push(`${seconds % 60}s`);
  } else {
    parts.push(`${seconds}s`);
  }

  return parts.join(' ');
}

export function timeAgo(value: string | number | Date) {
  return formatRelative(value, Date.now());
}

export function isToday(value: string | number | Date) {
  const date = new Date(value);
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

export function startOfDay(value: string | number | Date) {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function endOfDay(value: string | number | Date) {
  const d = new Date(value);
  d.setHours(23, 59, 59, 999);
  return d;
}

export function addDays(value: string | number | Date, days: number) {
  const d = new Date(value);
  d.setDate(d.getDate() + days);
  return d;
}

export function diffInDays(a: string | number | Date, b: string | number | Date) {
  const aDate = new Date(a);
  const bDate = new Date(b);
  const diff = startOfDay(aDate).getTime() - startOfDay(bDate).getTime();
  return Math.round(diff / (1000 * 60 * 60 * 24));
}

export function parseIso(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function toIsoDate(value: string | number | Date) {
  const d = new Date(value);
  return d.toISOString().split('T')[0];
}

export function formatRange(start: string | number | Date, end: string | number | Date, locale = 'en-US') {
  return `${formatDate(start, locale)} – ${formatDate(end, locale)}`;
}

export function minutesBetween(a: string | number | Date, b: string | number | Date) {
  const diff = new Date(a).getTime() - new Date(b).getTime();
  return Math.round(diff / (1000 * 60));
}

export function secondsBetween(a: string | number | Date, b: string | number | Date) {
  const diff = new Date(a).getTime() - new Date(b).getTime();
  return Math.round(diff / 1000);
}

export function isSameDay(a: string | number | Date, b: string | number | Date) {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

// ---------------------------------------------------------------------------
// CCMS SDK dates
//
// Every backend timestamp is the numeric form `yyyyMMddHHmmss` (e.g. 20260806143000).
// These are the canonical helpers for that format — the same pair is currently
// redefined locally in several list pages (see _memory/_context/06-known-issues.md D2);
// new code should import from here.
// ---------------------------------------------------------------------------

const pad2 = (n: number) => String(n).padStart(2, '0');

/** Format a Date/parsable value as the SDK's `yyyyMMdd000000` (midnight) filter bound. */
export function toSDKDate(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}000000`;
}

/** Format a Date/parsable value as a full `yyyyMMddHHmmss` SDK timestamp. */
export function toSDKDateTime(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return (
    `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}` +
    `${pad2(d.getHours())}${pad2(d.getMinutes())}${pad2(d.getSeconds())}`
  );
}

/** Render an SDK timestamp as `yyyy-MM-dd`, or an em dash when absent/malformed. */
export function formatSDKDate(value: string | number | null | undefined): string {
  const raw = String(value ?? '');
  if (raw.length < 8) return '—';
  return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
}

/** Parse an SDK timestamp into a Date, or null when absent/malformed. */
export function fromSDKDate(value: string | number | null | undefined): Date | null {
  const raw = String(value ?? '');
  if (raw.length < 8) return null;
  const d = new Date(
    Number(raw.slice(0, 4)),
    Number(raw.slice(4, 6)) - 1,
    Number(raw.slice(6, 8)),
    Number(raw.slice(8, 10) || 0),
    Number(raw.slice(10, 12) || 0),
    Number(raw.slice(12, 14) || 0),
  );
  return isNaN(d.getTime()) ? null : d;
}

/** The current moment as a full SDK timestamp. */
export function sdkNow(): string {
  return toSDKDateTime(new Date());
}

/** Midnight `days` ago as an SDK filter bound — for "opened in the last N days" ranges. */
export function sdkDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return toSDKDate(d);
}
